import { useState, useEffect } from "react";
import { TextField, Modal, Box, CircularProgress } from "@mui/material";
import cogoToast from "cogo-toast";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import TestimonialsCarousel from "../components/leadMagnet/TestimonialCarousal";
import bg0 from "../assets/images/leadMagnet/bg0.svg";
import bg1 from "../assets/images/leadMagnet/bg1.svg";
import bg2 from "../assets/images/leadMagnet/bg2.svg";
import { getLeadMagnet, postLeadMagnetResponseForm, downloadLeadMagnet } from "../api/utils";
import useFetch from "../api/hooks/useFetch";

// Modal styles
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "500px" },
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
  outline: "none",
};

// Hero Section Component
const HeroSection = ({
  content,
  offeringType,
  formData,
  handleInputChange,
  showError,
  isSubmitting,
  submitLeadForm,
  downloadLink,
  handleManualDownload,
  isFormSubmitted
}) => (
  <section
    className="page-hero-sec"
    style={{
      background: "#0A2458",
      minHeight: "80vh",
      display: "flex",
      alignItems: "center",
    }}
  >
    <div className="container">
      <div className="row align-items-center">
        <div className="col-lg-7 col-md-12">
          <div className="hero-content text-white">
            <p className="text-warning">
              {offeringType === "E-Book" ? "FREE E-BOOK" : "FREE WEBINAR"}
            </p>
            <h1
              className="head-font mb-4"
              style={{
                fontSize: "3.5rem",
                fontWeight: "bold",
                lineHeight: "1.2",
              }}
            >
              {content.title}
            </h1>
            <p
              className="fs-17 mb-4"
              style={{ color: "#e2e8f0", lineHeight: "1.6" }}
            >
              {content.subtitle}
            </p>
          </div>
        </div>
        <div className="col-lg-5 col-md-12">
          <div
            className="hero-form bg-white p-4 rounded shadow-lg"
            style={{ maxWidth: "400px", margin: "0 auto" }}
          >
            <div className="text-center mb-4">
              <h5 className="tc-primary head-font mb-2">
                {offeringType === "E-Book"
                  ? "Get the E-Book Now"
                  : "Register for Webinar"}
              </h5>
              <p className="text-muted small">
                {offeringType === "E-Book"
                  ? "Enter your details to download instantly"
                  : "Enter your details to reserve your spot"}
              </p>
            </div>

            {/* Success message for E-Book with download link */}
            {downloadLink && offeringType === "E-Book" && (
              <div className="alert alert-success text-center mb-4">
                <h6 className="text-success mb-2">✅ Success!</h6>
                <p className="mb-3 small">Your download is ready! Click the button below to download your E-Book:</p>
                <button
                  onClick={handleManualDownload}
                  className="btn btn-success btn-sm w-100"
                  style={{ fontWeight: "600" }}
                >
                  📥 Download E-Book Now
                </button>
                <p className="text-muted small mt-2 mb-0">
                  <strong>Note:</strong> This download link can only be used once for security.
                </p>
              </div>
            )}

            {/* Success message for Webinar */}
            {isFormSubmitted && offeringType === "Webinar" && (
              <div className="alert alert-success text-center mb-4">
                <h6 className="text-success mb-2">✅ Webinar Registration Successful!</h6>
                <p className="mb-0 small">
                  Thank you for registering! Check your email for webinar details and joining instructions.
                </p>
              </div>
            )}

            {/* Show form only if not submitted or if E-Book and no download link yet */}
            {(!isFormSubmitted || (offeringType === "E-Book" && !downloadLink)) && (
              <>
                <div className="mb-4">
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={showError && !formData.name.trim()}
                    helperText={
                      showError && !formData.name.trim() ? "Name is required" : ""
                    }
                    sx={{ backgroundColor: "#f8fafc" }}
                  />
                </div>
                <div className="mb-4">
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={showError && !formData.email.trim()}
                    helperText={
                      showError && !formData.email.trim()
                        ? "Valid email is required"
                        : ""
                    }
                    sx={{ backgroundColor: "#f8fafc" }}
                  />
                </div>
                <div className="mb-4">
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    type="text"                    
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={showError && !formData.phone.trim()}
                    helperText={
                      showError && !formData.phone.trim()
                        ? "Valid phone is required"
                        : ""
                    }
                    inputProps={{ maxLength: 10 }}
                    sx={{ backgroundColor: "#f8fafc" }}
                  />
                </div>

                <button
                  onClick={submitLeadForm}
                  className="btn btn-primary w-100"
                  disabled={isSubmitting}
                  style={{
                    background: "#1e3a8a",
                    border: "none",
                    padding: "12px",
                    fontWeight: "600",
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                      Submitting...
                    </>
                  ) : offeringType === "E-Book" ? (
                    "Get Your Free E-Book"
                  ) : (
                    "Register for Free"
                  )}
                </button>

                <p className="text-center text-muted small mt-2 mb-0">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Understanding Section Component
const UnderstandingSection = ({ content, isLoading, openModal }) => (
  <section
    className="py-4 d-flex align-items-center justify-content-center min-vh-100"
    style={{
      backgroundImage: `url(${bg0})`,
      backgroundPosition: "center",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
    }}
  >
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-lg-8 text-center">
          <h2 className="tc-primary head-font mb-4">{content.heading}</h2>
          <p className="fs-6 text-muted mb-4">{content.subheading}</p>
          <div
            className="position-relative mb-4 rounded bg-light overflow-hidden"
            style={{ paddingBottom: "56.25%" }}
          >
            {isLoading ? (
              <div className="position-absolute top-50 start-50 translate-middle">
                <CircularProgress />
              </div>
            ) : content.offering_type === "E-Book" ? (
              <img
                src={content.banner}
                alt="Banner"
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{ objectFit: "cover" }}
              />
            ) : (
              <div className="position-absolute top-50 start-50 translate-middle text-center">
                <div className="text-secondary mb-3 fs-1">▶</div>
                <p className="text-secondary m-0">
                  Webinar preview will load here
                </p>
              </div>
            )}
          </div>
          <button onClick={openModal} className="LM_link_btn">
            {content.offering_type === "E-Book"
              ? "Get Your Free E-Book"
              : "Register for Free"}
          </button>
        </div>
      </div>
    </div>
  </section>
);

// Key Points Section Component
const KeyPointsSection = ({ description, offeringType, openModal }) => (
  <section className="py-5">
    <div className="container text-center">
      <h3 className="tc-primary head-font mb-4">What You'll Learn:</h3>
      <div dangerouslySetInnerHTML={{ __html: description }} />
      <button onClick={openModal} className="LM_link_btn w-auto">
        {offeringType === "E-Book"
          ? "Get Your Free E-Book"
          : "Register for Free"}
      </button>
    </div>
  </section>
);

// Consequences Section Component
const ConsequencesSection = ({
  badEffects,
  helpOffer,
  offeringType,
  openModal,
}) => (
  <section
    className="py-4"
    style={{
      backgroundImage: `url(${bg1})`,
      backgroundPosition: "center",
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
    }}
  >
    <div className="container text-center">
      <h2 className="text-center fw-bold mb-5" style={{ fontSize: "28px" }}>
        Making a wrong choice in selecting a web development <br /> company can
        cost your business big time.
      </h2>
      <div className="row text-center mb-5 justify-content-center">
        {(badEffects || []).map((effect) => (
          <div key={effect.id} className="col-md-4 mb-4">
            <div className="border  bg-white rounded p-4 h-100 d-flex flex-column justify-content-center align-items-center">
              <img
                src={effect.icon}
                alt="Icon"
                style={{ width: "24px", height: "40px" }}
              />
              <p className="mt-3 mb-0">{effect.text}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mb-4">
        <h5 className="fw-bold">
          {offeringType === "E-Book"
            ? "This E-Book will help"
            : "This Webinar will help"}
        </h5>
        <p className="text-muted mx-auto" style={{ maxWidth: "800px" }}>
          {helpOffer}
        </p>
      </div>
      <button onClick={openModal} className="LM_link_btn">
        {offeringType === "E-Book"
          ? "Get Your Free E-Book"
          : "Register for Free"}
      </button>
    </div>
  </section>
);

// Testimonials Section Component
const TestimonialsSection = ({ reviews, offeringType }) => (
  <section
    className="py-5 "
    style={{
      backgroundImage: `url(${bg2})`,
      backgroundPosition: "",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      display: "flex",
      alignItems: "",
      justifyContent: "center",
    }}
  >
    <div className="container">
      <div className="text-center mb-5">
        <h2 className="tc-primary head-font">
          Read what Business Owners have to say about this Super Informative{" "}
          {offeringType === "E-Book" ? "E-Book" : "Webinar"} that will help you
          save a lot of Time, Energy & Money.
        </h2>
      </div>
      <TestimonialsCarousel
        testimonials={(reviews || []).map((review) => ({
          id: review.id,
          name: review.name,
          role: review.designation,
          content: review.content,
          avatar: review.image,
        }))}
      />
    </div>
  </section>
);

// Final CTA Section Component
const FinalCtaSection = ({ offeringType, openModal }) => (
  <section className="py-5" style={{ background: "#0b1d51" }}>
    <div className="container">
      <div className="text-center text-white">
        <h2 className="fw-bold mb-3 display-6">What Are You Waiting For?</h2>
        <p className="mb-4" style={{ fontSize: "1.125rem", opacity: 0.9 }}>
          this {offeringType === "E-Book" ? "e-book" : "webinar"} will help you
          <br />
          find & evaluate the best website development company
          <br />
          that will help you to create your website you need to grow your
          business
        </p>
        <div className="d-flex justify-content-center gap-3 flex-wrap">
          <button onClick={openModal} className="LM_link_btn_white">
            {offeringType === "E-Book"
              ? "Get Free E-Book"
              : "Register for Free"}
          </button>
        </div>
      </div>
    </div>
  </section>
);

// Lead Capture Modal Component
const LeadCaptureModal = ({
  isOpen,
  closeModal,
  formData,
  handleInputChange,
  showError,
  isSubmitting,
  submitLeadForm,
  offeringType,
  downloadLink,
  handleManualDownload,
  isFormSubmitted
}) => (
  <Modal
    open={isOpen}
    onClose={closeModal}
    aria-labelledby="lead-capture-modal"
    aria-describedby="lead-capture-form"
  >
    <Box sx={modalStyle}>
      <div className="text-center mb-4">
        <h4 className="tc-primary head-font">
          {offeringType === "E-Book"
            ? "Get Your Free E-Book"
            : "Register for Free Webinar"}
        </h4>
        <p className="text-muted">
          {offeringType === "E-Book"
            ? "Enter your details below and we'll send the PDF guide directly to your inbox."
            : "Enter your details below to reserve your spot in our exclusive webinar."}
        </p>
      </div>

      {downloadLink && offeringType === "E-Book" && (
        <div className="alert alert-success text-center mb-4">
          <h6 className="text-success mb-2">✅ Success!</h6>
          <p className="mb-3 small">Your download is ready! Click the button below to download your E-Book:</p>
          <button
            onClick={handleManualDownload}
            className="btn btn-success btn-sm w-100"
            style={{ fontWeight: "600" }}
          >
            📥 Download E-Book Now
          </button>
          <p className="text-muted small mt-2 mb-0">
            <strong>Note:</strong> This download link can only be used once for security.
          </p>
        </div>
      )}

      {/* Success message for Webinar */}
      {isFormSubmitted && offeringType === "Webinar" && (
        <div className="alert alert-success text-center mb-4">
          <h6 className="text-success mb-2">✅ Webinar Registration Successful!</h6>
          <p className="mb-0 small">
            Thank you for registering! Check your email for webinar details and joining instructions.
          </p>
        </div>
      )}

      {(!isFormSubmitted || (offeringType === "E-Book" && !downloadLink)) && (
        <>
          <div className="mb-3">
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={showError && !formData.name.trim()}
              helperText={
                showError && !formData.name.trim() ? "Name is required" : ""
              }
              margin="dense"
              sx={{ "& input": { background: "#f8fafc" } }}
              inputProps={{ maxLength: 100 }}
            />
          </div>
          <div className="mb-4">
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={showError && !formData.email.trim()}
              helperText={
                showError && !formData.email.trim() ? "Valid email is required" : ""
              }
              margin="dense"
              sx={{ "& input": { background: "#f8fafc" } }}
              inputProps={{ maxLength: 150 }}
            />
          </div>
          <div className="mb-4">
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              type="text"
              value={formData.phone}
              onChange={handleInputChange}
              error={showError && !formData.phone.trim()}
              helperText={
                showError && !formData.phone.trim() ? "Valid phone is required" : ""
              }
              margin="dense"
              sx={{ "& input": { background: "#f8fafc" } }}
              inputProps={{ maxLength: 10 }}
            />
          </div>
          <div className="d-flex gap-2">
            <button
              onClick={closeModal}
              className="btn btn-outline-secondary flex-fill"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={submitLeadForm}
              className="btn btn-primary flex-fill"
              disabled={isSubmitting}
              style={{
                background: "#1e3a8a",
                border: "none",
                position: "relative",
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                  Sending...
                </>
              ) : offeringType === "E-Book" ? (
                "Send Me The E-Book"
              ) : (
                "Register for Webinar"
              )}
            </button>
          </div>
          <p className="text-center text-muted small mt-3 mb-0">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </>
      )}
    </Box>
  </Modal>
);

const LeadMagnet = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadLink, setDownloadLink] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false); // New state
  const [contentData, setContentData] = useState({
    data: null,
    error: null,
  });

  const [slug, setSlug] = useState(() => {
    const path = window.location.pathname;
    const match = path.match(/\/lm\/(.+)/);
    return match ? match[1] : "";
  });

  const {
    apiData: leadMagnet,
    isLoading: isLeadMagnetLoding,
    error: leadMagnetError,
  } = useFetch(getLeadMagnet, slug);

  useEffect(() => {
    if (leadMagnetError) {
      cogoToast.error("Failed to load content");
    }
    if (leadMagnet.status === "error") {
      cogoToast.error("Failed to load content");
    }
  }, [leadMagnetError, leadMagnet]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Function to open the modal which collects lead information
  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ name: "", email: "", phone: "" });
    setShowError(false);
  };

  // Updated submitLeadForm function
  const submitLeadForm = async () => {
    const { name, email, phone } = formData;

    if (!name.trim() || !email.trim() || !phone.trim()) {
      setShowError(true);
      cogoToast.info("Please fill in all required fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setShowError(true);
      cogoToast.error("Please enter a valid email address");
      return;
    }

    const phoneRegex = /^\d{10}$/;
  if (!phoneRegex.test(phone)) {
    setShowError(true);
    cogoToast.error("Phone number must be exactly 10 digits");
    return;
  }

    setIsSubmitting(true);

    try {
      const payload = {
        name,
        email,
        phone,
        slug,
        offering_type: leadMagnet?.offering_type,
      };

      const response = await postLeadMagnetResponseForm(payload);

      if (response.status === "ok") {
        if (leadMagnet?.offering_type === "E-Book" && response.data?.download_token) {
          setDownloadLink(response.data.download_token);

          setFormData({ name: "", email: "", phone: "" });
          setShowError(false);
          setIsFormSubmitted(true);

          cogoToast.success("Thank you! Your download link is now available below.");

        } else if (leadMagnet?.offering_type === "Webinar") {
          cogoToast.success("Webinar registration successful! Check your email for details.");
          setFormData({ name: "", email: "", phone: "" });
          setShowError(false);
          setIsFormSubmitted(true);
          closeModal();
        }
      } else {
        cogoToast.error(response.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error('Form submission error:', err);
      cogoToast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualDownload = async () => {
    if (!downloadLink) {
      cogoToast.error("No download token available");
      return;
    }

    try {
      const downloadResult = await downloadLeadMagnet(slug,downloadLink);

      if (downloadResult.success) {
        // Create download link and trigger download
        const link = document.createElement('a');
        link.href = downloadResult.fileUrl;
        link.download = downloadResult.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        cogoToast.success("Download started! Check your Downloads folder.");

        // Clear the download link since it's one-time use
        setDownloadLink("");

        // Clean up the blob URL after some time
        setTimeout(() => {
          window.URL.revokeObjectURL(downloadResult.fileUrl);
        }, 30000); // 30 seconds

      } else {
        cogoToast.error(downloadResult.error || "Download failed. Please contact our team.");
        // Clear the download link if it failed (likely used already)
        setDownloadLink("");
      }
    } catch (error) {
      console.error('Download error:', error);
      cogoToast.error("Download failed. Please try again.");
    }
  };

  if (isLeadMagnetLoding) {
    return (
      <div
        className="container text-center"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (contentData.error) {
    return (
      <div className="container text-center py-5">
        <h3>Error loading the page</h3>
        <p>{contentData.error}</p>
      </div>
    );
  }

  return (
    <div>
      <main className="wrapper">
        <HeroSection
          content={{ title: leadMagnet?.title, subtitle: leadMagnet?.subtitle }}
          offeringType={leadMagnet?.offering_type}
          formData={formData}
          handleInputChange={handleInputChange}
          showError={showError}
          isSubmitting={isSubmitting}
          submitLeadForm={submitLeadForm}
          downloadLink={downloadLink}
          handleManualDownload={handleManualDownload}
          isFormSubmitted={isFormSubmitted}
        />
        <UnderstandingSection
          content={leadMagnet}
          isLoading={isLeadMagnetLoding}
          openModal={openModal}
        />
        <KeyPointsSection
          description={leadMagnet?.description}
          offeringType={leadMagnet?.offering_type}
          openModal={openModal}
        />
        <ConsequencesSection
          badEffects={leadMagnet?.bad_effects}
          helpOffer={leadMagnet?.help_offer}
          offeringType={leadMagnet?.offering_type}
          openModal={openModal}
        />
        <TestimonialsSection
          reviews={leadMagnet?.reviews}
          offeringType={leadMagnet?.offering_type}
        />
        <FinalCtaSection
          offeringType={leadMagnet?.offering_type}
          openModal={openModal}
        />
        <LeadCaptureModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          formData={formData}
          handleInputChange={handleInputChange}
          showError={showError}
          isSubmitting={isSubmitting}
          submitLeadForm={submitLeadForm}
          downloadLink={downloadLink}
          handleManualDownload={handleManualDownload}
          isFormSubmitted={isFormSubmitted}
          offeringType={leadMagnet?.offering_type}
        />
      </main>
    </div>
  );
};

export default LeadMagnet;